import { useCallback, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FopiFund,
  FopiFundShareClass,
} from '@asset-metrix/portal-gateway-client-js';
import {
  Button,
  ConditionalFilterPlugin,
  ErrorMessage,
  FetchOptionsFunction,
  fileReducer,
  getConditionalOptions,
  GoogleFontIcon,
  InlineAlert,
  jsonEditorWidgets,
  MIME_EXT_MAPPING_CSV,
  MIME_EXT_MAPPING_EXCEL,
  MIME_EXT_MAPPING_PDF,
  MIME_EXT_MAPPING_WORD,
  Modal,
  SumPlugin,
  Upload,
} from '@asset-metrix/portal-ui';
import gp from '@sagold/json-pointer';
import {
  isJsonError,
  JsonError,
  JsonSchema,
  SetOptionTitleToPlugin,
  useEditor,
  useEditorPlugin,
  Widget,
} from '@sagold/react-json-editor';
import { MutationStatus, QueryStatus } from '@tanstack/react-query';
import clsx from 'clsx';
import { z } from 'zod';

import { pixelSizeHelperPortalUi } from '../../utils/pixelSizeHelperPortalUi';

import { useFundShareClassTableDataQuery } from './useFundShareClassTableDataQuery';
import { useFundTableDataQuery } from './useFundTableDataQuery';
import { useGetFopiCurrencyData } from './useGetFopiCurrencyData';

import styles from './initFundOperationModal.module.scss';

type FopiFormData = {
  Fund_Object_Unique_Id: string;
};

const FormDataType = z.object({
  shareClasses: z
    .array(
      z.object({
        Share_Object_Unique_Id: z.string(),
        sumTotal: z.unknown(),
        investors: z
          .array(
            z.object({
              selected: z.unknown(),
              investor: z.unknown(),
              investorId: z.unknown(),
            })
          )
          .optional(),
      })
    )
    .optional(),
});
type FormDataType = z.infer<typeof FormDataType>;

export type InitFundOperationModalComponentProps = {
  onClose: () => void;
  opened: boolean;
  onApply: (data: Record<string, unknown>, files: File[]) => void;
  jsonSchema: JsonSchema;
  createFopiTableRowStatus: MutationStatus;
  errorMessage?: string;
  fetchOptions: FetchOptionsFunction;
  fundStatus: QueryStatus;
};

function isFormDataType(formData: unknown): formData is FormDataType {
  return FormDataType.safeParse(formData).success;
}

function mapIncludedInvestorAccounts(
  formData: FormDataType,
  shareClassesInfos?: FopiFundShareClass[]
) {
  return {
    ...formData,
    shareClasses: formData.shareClasses?.map((sc) => {
      const includedInvestorIds = sc.investors
        ?.filter((sci) => sci.selected)
        .map((sci) => sci.investorId)
        .filter((investorId) => typeof investorId === 'string');

      if (includedInvestorIds == null) {
        throw new Error('Failed to extract included investor IDs');
      }

      const scInfo = shareClassesInfos?.find(
        (info) => sc.Share_Object_Unique_Id === info.shareClassId
      );

      if (scInfo == null) {
        console.error('Invalid share class: %o', sc);
        throw new Error('Could not find share class in backend response');
      }

      const incluedInvestorAccounts = scInfo.investors
        .filter((investor) => includedInvestorIds.includes(investor.investorId))
        .flatMap((investor) =>
          investor.investorAccounts.map((ia) => ({
            Investor_Name: investor.investor,
            Investor_Object_Unique_Id: investor.investorId,
            InvestAcc_Name: ia.investorAccount,
            InvestAcc_Object_Unique_ID: ia.investorAccountId,
          }))
        );

      const { investors, ...scWithoutInvestors } = sc;

      return {
        ...scWithoutInvestors,
        InvestAcc: incluedInvestorAccounts,
      };
    }),
  };
}

function transformFormData(
  formData: unknown,
  shareClasses?: FopiFundShareClass[]
): ReturnType<typeof mapIncludedInvestorAccounts> {
  if (!isFormDataType(formData)) {
    throw new Error('formData is not FormDataType.');
  }

  for (const sc of formData.shareClasses || []) {
    delete sc.sumTotal;
  }

  return mapIncludedInvestorAccounts(formData, shareClasses);
}

export function InitFundOperationModalComponent({
  onClose,
  opened,
  onApply,
  jsonSchema,
  createFopiTableRowStatus,
  errorMessage,
  fetchOptions,
  fundStatus,
}: InitFundOperationModalComponentProps) {
  const [selectedFundId, setSelectedFundId] = useState<string>();
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<JsonError[]>([]);
  const [fileSelection, dispatch] = useReducer(fileReducer, []);
  const [transformFormDataError, setTransformFormDataError] = useState(false);

  // Get the currency data for FOPI from BE
  const { data: currencyData, status: currencyStatus } =
    useGetFopiCurrencyData(selectedFundId);

  useEffect(() => {
    // Reset state of files in case of error, just like
    // rest of the modal.
    if (createFopiTableRowStatus === 'error') {
      dispatch({
        type: 'updateAllUploadStates',
        payload: {
          newState: 'initial',
        },
      });
    }
  }, [createFopiTableRowStatus]);

  const editor = useEditor<FopiFormData>({
    widgets: jsonEditorWidgets,
    schema: jsonSchema,
    onChange: (data, _root, editor) => {
      setSelectedFundId(data.Fund_Object_Unique_Id);
      setValidationErrors(editor.getErrors());
    },
    validate: false,
    addOptionalProps: true,
    plugins: [SetOptionTitleToPlugin],
  });

  useEditorPlugin(editor, ConditionalFilterPlugin, {
    fetchOptions,
  });
  useEditorPlugin(editor, SumPlugin);

  // Set Currency drop down value into the editor
  useEffect(() => {
    if (!editor) {
      return;
    }

    const jsonSchema = editor.draft.getSchema();
    if (!jsonSchema) {
      return;
    }

    const updateSchema = (isLoading: boolean, values?: string[]) => {
      const currencyPointer = '/properties/Operation_Currency';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const schema = gp.get<any>(jsonSchema, currencyPointer);

      if (isLoading) {
        schema.options = { ...schema.options, loading: true };
      } else if (values) {
        schema.enum = values;
        schema.options = {
          ...schema.options,
          enum: values,
          loading: false,
        };
      }

      gp.set(jsonSchema, currencyPointer, schema);

      if (!isJsonError(jsonSchema)) {
        editor.setSchema(jsonSchema);
      }
      editor.validate();
    };

    if (currencyStatus === 'error') {
      console.error('Error fetching currency data:', currencyStatus);
      updateSchema(false);
      return;
    }

    if (currencyStatus === 'pending') {
      updateSchema(true);
      return;
    }

    if (currencyStatus === 'success' && currencyData?.data?.items) {
      const currencyValues = currencyData.data.items.map((c) => c.value);
      updateSchema(false, currencyValues);
    }
  }, [editor, currencyData, currencyStatus]);

  const { data: shareClassesData } = useFundShareClassTableDataQuery({
    selection: { ...(selectedFundId != null && { fundId: selectedFundId }) },
    enabled: !!selectedFundId,
  });

  useEffect(() => {
    if (editor) {
      editor.setValue(
        '#/shareClasses',
        (shareClassesData?.data.items ?? []).map((sc) => ({
          Share: sc.shareClassName,
          Share_Object_Unique_Id: sc.shareClassId,
          investors: sc.investors,
        }))
      );
    }
  }, [shareClassesData?.data.items, editor]);

  const apply = useCallback(() => {
    if (!editor) {
      console.error(
        'Cannot apply init fund operation due to missing editor instance.'
      );
      return;
    }

    if (validationErrors.length > 0) {
      console.error(
        'Cannot apply init fund operation due to validations errors.',
        validationErrors
      );
      return;
    }

    setTransformFormDataError(false);
    dispatch({
      type: 'updateAllUploadStates',
      payload: {
        newState: 'isPending',
      },
    });

    const formData = editor.getData();

    try {
      const data = transformFormData(formData, shareClassesData?.data.items);
      const files = fileSelection.map((f) => f.file);
      onApply(data, files);
    } catch (e) {
      console.error(e);
      setTransformFormDataError(true);
      dispatch({
        type: 'updateAllUploadStates',
        payload: {
          newState: 'initial',
        },
      });
    }
  }, [onApply, editor, fileSelection, validationErrors, shareClassesData]);

  return (
    <Modal
      actions={
        <>
          <Button
            variant="secondary"
            icon={<GoogleFontIcon name="close" />}
            onClick={onClose}
          >
            {t('app.initFundOperation.cancel')}
          </Button>
          <Button
            disabled={
              validationErrors.length > 0 ||
              fileSelection.length === 0 ||
              fundStatus === 'pending' ||
              fundStatus === 'error' ||
              currencyStatus === 'pending' ||
              currencyStatus === 'error'
            }
            loading={createFopiTableRowStatus === 'pending'}
            variant="primary"
            onClick={apply}
          >
            {t('app.initFundOperation.submit')}
          </Button>
        </>
      }
      modalType="action"
      opened={opened}
      onClose={onClose}
      size={pixelSizeHelperPortalUi(650)}
      title={t('app.initFundOperation.title')}
    >
      {fundStatus === 'error' && (
        <InlineAlert className={styles['alert']}>
          {t('app.initFundOperation.fundLoadingError')}
        </InlineAlert>
      )}
      {currencyStatus === 'error' && (
        <InlineAlert className={styles['alert']}>
          {t('app.initFundOperation.currencyLoadingError')}
        </InlineAlert>
      )}
      <div
        className={clsx(
          styles['content'],
          'portal-form rje-form rje-theme rje-theme--light'
        )}
      >
        <Upload
          fileState={fileSelection}
          dispatch={dispatch}
          accept={[
            ...Object.keys(MIME_EXT_MAPPING_CSV),
            ...Object.keys(MIME_EXT_MAPPING_EXCEL),
            ...Object.keys(MIME_EXT_MAPPING_PDF),
            ...Object.keys(MIME_EXT_MAPPING_WORD),
          ]}
        />
        {fileSelection.length === 0 && (
          <ErrorMessage className={styles['input-section__upload-error']}>
            {t('app.table.workflowActionModal.fileUploadRequired')}
          </ErrorMessage>
        )}
        <Widget
          editor={editor}
          options={{
            disabled: createFopiTableRowStatus === 'pending',
          }}
        />
        {(createFopiTableRowStatus === 'error' || transformFormDataError) && (
          <ErrorMessage className={styles['message']}>
            {errorMessage || t('app.initFundOperation.error')}
          </ErrorMessage>
        )}
      </div>
    </Modal>
  );
}

type ConditionalOptions = Partial<
  Record<keyof FopiFund, { values: string[]; labels: (string | undefined)[] }>
>;

export type InitFundOperationModalProps = {
  errorMessage?: string;
  jsonSchema: JsonSchema;
  onApply: (data: Record<string, unknown>, files: File[]) => void;
  onClose: () => void;
  opened: boolean;
  createFopiTableRowStatus: MutationStatus;
};

export function InitFundOperationModal({
  ...props
}: InitFundOperationModalProps) {
  const { data: fundData, status: fundStatus } = useFundTableDataQuery(true);

  const fetchOptions = useCallback<FetchOptionsFunction>(
    async (filterObject: Partial<FopiFund>) => {
      return new Promise((resolve, reject) => {
        if (fundStatus === 'success') {
          const filterProperties: (keyof FopiFund)[] = [
            'fundId',
            'fundCategory',
          ];
          const conditionalOptions: ConditionalOptions = {};
          for (const filter of filterProperties) {
            conditionalOptions[filter] = getConditionalOptions(
              fundData?.data.items ?? [],
              [filter, filter === 'fundId' ? 'fundName' : undefined],
              filterObject
            );
          }
          resolve(conditionalOptions);
        } else if (fundStatus === 'error') {
          reject();
        }
      });
    },
    [fundData, fundStatus]
  );

  return (
    <InitFundOperationModalComponent
      fundStatus={fundStatus}
      fetchOptions={fetchOptions}
      {...props}
    />
  );
}
